/* eslint-env node */
// Slug canónico por producto: la identidad estable que usa /producto/<slug>.
//
// CommonJS a propósito: este módulo lo consume el cliente (webpack) Y el
// prerender (node scripts/prerender.js). Si cada uno tuviera su propia copia de
// la regla, un día divergirían y el link compartido dejaría de resolver.
//
// Los nombres NO son únicos entre categorías ("Nutella" es brazo y galleta,
// "Tres Leches" es brazo y pastel), así que el slug lleva el tipo delante.

// Tipo de producto (el mismo string que recibe openProductDetail) → prefijo.
const TYPE_PREFIX = {
  brazo: 'brazo',
  galleta: 'galleta',
  galleta_especial: 'galleta-especial',
  panaderia: 'panaderia',
  desayuno: 'desayuno',
  menu_sal: 'menu',
  bebida: 'bebida',
  pastel: 'pastel',
};

// Colección de useProducts → tipo. El orden importa: fija el ganador cuando dos
// productos derivan el mismo slug.
const COLLECTIONS = [
  ['brazosGitanos', 'brazo'],
  ['galletas', 'galleta'],
  ['galletasEspeciales', 'galleta_especial'],
  ['pasteles', 'pastel'],
  ['panaderia', 'panaderia'],
  ['desayunos', 'desayuno'],
  ['menuSalado', 'menu_sal'],
  ['bebidasFrias', 'bebida'],
  ['bebidasCalientes', 'bebida'],
];

// "Piña Colada" → "pina-colada". Quita acentos, baja a minúsculas y colapsa
// todo lo que no sea alfanumérico en un guión.
const slugify = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// Slug de un producto suelto, sin resolver colisiones (para eso, buildProductIndex).
//
// Si el producto trae `slug` de la BD (la-reina, tres-leches — ya se usan en
// CakesSection y Home) se respeta como cuerpo del slug, pero el prefijo de tipo
// se antepone igual: así la regla es una sola y no hay forma de que un slug de
// la BD choque con uno derivado.
const productSlug = (product, type) => {
  const prefix = TYPE_PREFIX[type];
  const body = slugify(product?.slug || product?.name);
  if (!prefix || !body) return null;
  return `${prefix}-${body}`;
};

// Índice slug → { product, type, collection } sobre el objeto que devuelve
// useProducts. Determinista: recorre las colecciones en orden fijo y, si dos
// productos derivan el mismo slug, el segundo recibe un sufijo numérico.
const buildProductIndex = (products) => {
  // Object.create(null), NO {}: el slug viene de la URL, y con un objeto normal
  // index['constructor'] o index['toString'] devolverían algo truthy heredado de
  // Object.prototype. Los consumidores lo tomarían por un producto y reventarían
  // al desestructurarlo (en la home eso tumba el carrito entero).
  const index = Object.create(null);
  if (!products) return index;

  for (const [collection, type] of COLLECTIONS) {
    const items = products[collection];
    if (!Array.isArray(items)) continue;

    for (const product of items) {
      const base = productSlug(product, type);
      if (!base) continue;

      let slug = base;
      let n = 2;
      while (index[slug]) slug = `${base}-${n++}`;

      index[slug] = { product, type, collection, slug };
    }
  }

  return index;
};

// Slug de un producto que ya está en el índice. Va por el índice y no por
// productSlug() directo para no perderse el sufijo cuando hubo colisión.
const slugFor = (index, product, type) => {
  if (!product) return null;
  if (index) {
    for (const key of Object.keys(index)) {
      const entry = index[key];
      if (entry.type !== type) continue;
      if (entry.product === product) return key;
      if (product.id && entry.product.id === product.id) return key;
    }
  }
  return productSlug(product, type);
};

module.exports = { slugify, productSlug, buildProductIndex, slugFor, TYPE_PREFIX, COLLECTIONS };
